import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, X, RefreshCcw, Calendar, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney, titleCase } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/permissions";

export default function BookingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [bk, setBk] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBy, setCancelBy] = useState("customer");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmt, setRefundAmt] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState("");
  const [reschedTime, setReschedTime] = useState("");
  const [reschedReason, setReschedReason] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/bookings/${id}`);
      setBk(r.data);
    } catch {
      toast.error("Failed to load booking");
    }
  };
  useEffect(() => { load(); }, [id]);

  const doAction = async (action, { reason }) => {
    try {
      await api.post(`/bookings/${id}/${action}`, { reason, note: reason });
      toast.success(`Booking ${action}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const submitRefund = async () => {
    if (!refundReason.trim()) return;
    setRefundLoading(true);
    try {
      await api.post("/admin/refund-requests", {
        booking_id: id,
        amount: refundAmt ? Number(refundAmt) : null,
        reason: refundReason.trim(),
      });
      toast.success("Refund request submitted for approval");
      setRefundOpen(false); setRefundAmt(""); setRefundReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally { setRefundLoading(false); }
  };

  const submitReschedule = async () => {
    if (!reschedDate || !reschedTime) return;
    try {
      await api.post(`/bookings/${id}/reschedule`, { new_date: reschedDate, new_time: reschedTime, reason: reschedReason });
      toast.success("Booking rescheduled");
      setReschedOpen(false); setReschedDate(""); setReschedTime(""); setReschedReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reschedule failed");
    }
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/bookings/${id}/note`, { note });
      toast.success("Note added");
      setNoteOpen(false); setNote("");
      load();
    } catch {
      toast.error("Failed to add note");
    }
  };

  if (!bk) return <div className="py-10 text-sm text-muted-foreground" data-testid="booking-loading">Loading…</div>;
  const canCancel = can(user, "booking.cancel");
  const canRefund = can(user, "booking.refund");
  const canResched = can(user, "booking.reschedule");
  const canConfirm = can(user, "booking.confirm");
  const canNote = can(user, "booking.note");

  return (
    <div data-testid="booking-detail-page">
      <button onClick={() => nav("/bookings")} className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-bookings">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to bookings
      </button>
      <PageHeader
        title={<span className="font-mono">{bk.reference}</span>}
        description={`${bk.experience_title} · ${bk.supplier_name}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={bk.status} />
            <StatusBadge status={bk.payment_status} />
            {canConfirm && bk.status === "pending" && (
              <Button size="sm" onClick={() => setConfirmDialog({ action: "confirm", title: "Confirm booking?", confirm: "Confirm" })} data-testid="booking-confirm-btn"><CheckCircle2 className="w-4 h-4" /> Confirm</Button>
            )}
            {canCancel && !["cancelled", "refunded", "completed"].includes(bk.status) && (
              <Button size="sm" variant="destructive" onClick={() => setCancelOpen(true)} data-testid="booking-cancel-btn"><X className="w-4 h-4" /> Cancel</Button>
            )}
            {canRefund && bk.payment_status !== "refunded" && bk.payment_status !== "failed" && (
              <Button size="sm" variant="outline" onClick={() => setRefundOpen(true)} data-testid="booking-refund-btn"><RefreshCcw className="w-4 h-4" /> Refund</Button>
            )}
            {canResched && !["cancelled", "refunded", "completed"].includes(bk.status) && (
              <Button size="sm" variant="outline" onClick={() => setReschedOpen(true)} data-testid="booking-reschedule-btn"><Calendar className="w-4 h-4" /> Reschedule</Button>
            )}
            {canNote && (
              <Button size="sm" variant="outline" onClick={() => setNoteOpen(true)} data-testid="booking-note-btn"><StickyNote className="w-4 h-4" /> Add note</Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <InfoCard title="Customer">
          <Row label="Name" value={<Link to={`/customers/${bk.customer_id}`} className="hover:underline">{bk.customer_name}</Link>} />
          <Row label="Email" value={bk.customer_email} />
          <Row label="Currency" value={bk.customer_currency} />
        </InfoCard>
        <InfoCard title="Experience">
          <Row label="Title" value={<Link to={`/experiences/${bk.experience_id}`} className="hover:underline">{bk.experience_title}</Link>} />
          <Row label="Supplier" value={<Link to={`/suppliers/${bk.supplier_id}`} className="hover:underline">{bk.supplier_name}</Link>} />
          <Row label="Activity date" value={`${bk.activity_date} ${bk.activity_time}`} />
        </InfoCard>
        <InfoCard title="Payment summary">
          <Row label="Amount paid" value={fmtMoney(bk.amount_paid, bk.currency)} />
          <Row label="Commission" value={fmtMoney(bk.commission_amount, bk.currency)} />
          <Row label="Supplier payable" value={fmtMoney(bk.supplier_payable, bk.currency)} />
          <Row label="Travelers" value={bk.travelers_count} />
          <Row label="Voucher" value={<StatusBadge status={bk.voucher_status} />} />
        </InfoCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <InfoCard title="Travelers">
          <ul className="text-sm space-y-1">
            {(bk.traveler_names || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </InfoCard>
        <div className="lg:col-span-2 bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border"><h3 className="text-sm font-semibold">Timeline</h3></div>
          <div className="divide-y divide-border">
            {[...(bk.timeline || [])].reverse().map((t, i) => (
              <div key={i} className="px-5 py-3 flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-medium">{t.action}</div>
                  {t.note && <div className="text-xs text-muted-foreground mt-0.5">{t.note}</div>}
                </div>
                <div className="text-xs text-muted-foreground text-right shrink-0">
                  <div>{t.actor}</div><div>{fmtDate(t.timestamp, true)}</div>
                </div>
              </div>
            ))}
            {(!bk.timeline || bk.timeline.length === 0) && <div className="px-5 py-8 text-center text-xs text-muted-foreground">No activity yet.</div>}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDialog}
        onOpenChange={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        confirmLabel={confirmDialog?.confirm}
        requireReason={confirmDialog?.reasonRequired}
        destructive={confirmDialog?.destructive}
        onConfirm={(payload) => doAction(confirmDialog.action, payload)}
      />

      {/* Cancel dialog with cancelled_by */}
      <Dialog open={cancelOpen} onOpenChange={(v) => { if (!cancelLoading) setCancelOpen(v); }}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel booking</DialogTitle>
            <DialogDescription>Record who initiated the cancellation — this affects overview reporting and supplier KPIs.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Cancelled by <span className="text-destructive">*</span></Label>
              <Select value={cancelBy} onValueChange={setCancelBy}>
                <SelectTrigger data-testid="cancel-by-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="platform">Platform (us)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reason <span className="text-destructive">*</span></Label>
              <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} placeholder="Required for audit log" data-testid="cancel-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelLoading}>Keep booking</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!cancelReason.trim()) return;
                setCancelLoading(true);
                try {
                  await api.post(`/bookings/${id}/cancel`, { reason: cancelReason, cancelled_by: cancelBy });
                  toast.success("Booking cancelled");
                  setCancelOpen(false); setCancelReason(""); setCancelBy("customer");
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Cancel failed");
                } finally {
                  setCancelLoading(false);
                }
              }}
              disabled={cancelLoading || !cancelReason.trim()}
              data-testid="cancel-submit-btn"
            >
              {cancelLoading ? "Cancelling…" : "Cancel booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Request refund</DialogTitle>
            <DialogDescription>Leave amount blank for a full refund of {fmtMoney(bk.amount_paid, bk.currency)}. An authorised admin must approve it before Stripe is charged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="refund-amt" className="text-xs">Amount ({bk.currency})</Label>
              <Input id="refund-amt" type="number" placeholder={`${bk.amount_paid} (full)`} value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} data-testid="refund-amount-input" />
            </div>
            <div>
              <Label htmlFor="refund-reason" className="text-xs">Reason <span className="text-destructive">*</span></Label>
              <Textarea id="refund-reason" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} rows={3} placeholder="Required for audit log" data-testid="refund-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)} disabled={refundLoading}>Cancel</Button>
            <Button onClick={submitRefund} disabled={refundLoading || !refundReason.trim()} data-testid="refund-submit-btn">
              {refundLoading ? "Submitting…" : "Submit for approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={reschedOpen} onOpenChange={setReschedOpen}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle>Reschedule booking</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">New date <span className="text-destructive">*</span></Label>
              <Input type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} data-testid="reschedule-date-input" />
            </div>
            <div>
              <Label className="text-xs">New time <span className="text-destructive">*</span></Label>
              <Input type="time" value={reschedTime} onChange={(e) => setReschedTime(e.target.value)} data-testid="reschedule-time-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Reason</Label>
              <Textarea value={reschedReason} onChange={(e) => setReschedReason(e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedOpen(false)}>Cancel</Button>
            <Button onClick={submitReschedule} disabled={!reschedDate || !reschedTime} data-testid="reschedule-submit-btn">Reschedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader><DialogTitle>Add internal note</DialogTitle></DialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Add operational context…" data-testid="note-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button onClick={submitNote} disabled={!note.trim()} data-testid="note-submit-btn">Add note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{title}</h3>
      <dl className="space-y-2">{children}</dl>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-sm text-right break-all">{value || "—"}</dd>
    </div>
  );
}
